import { Box, Button } from '@mui/material'
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef } from 'material-react-table'
import { useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useConfirmDialogContext } from '../../components/ConfirmDialog'
import { useGetCustomersQuery } from '../../app/redux-fetch/apiQuery'
import {
  useDeleteLoadTemplateMutation,
  useGetAllLoadTemplatesQuery,
} from '../../app/redux-fetch/apiOrders'
import AddButton from '../../components/Buttons/AddButton'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import FormDropdown from '../../components/FormDropDown/FormDropdown'
import MRTDataGridV2 from '../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../components/Typographies/TableTitleTypography'
import YesNoTypography from '../../components/YesNoTypograpphy/YesNoTypography'
import LoadTemplateForm from './LoadTemplateForm'
import messages from './messages'
import { ILoadTemplate } from './types'

const customerMessages = defineMessages({
  customer: {
    id: 'customer',
    defaultMessage: 'Customer',
  },
})

const LoadTemplateTable = () => {
  const { tenantId, customerId: loggedInCustomerId } = useLoggedInUser()
  const [isModalOpen, setModalOpen] = useState(false)
  const [isViewMode, setIsViewMode] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<ILoadTemplate | undefined>(undefined)
  const { formatMessage } = useIntl()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const [deleteLoadTemplate] = useDeleteLoadTemplateMutation()
  const { confirm } = useConfirmDialogContext()

  const isTenant = !!tenantId && !loggedInCustomerId
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(
    loggedInCustomerId,
  )
  const customerId = isTenant ? selectedCustomerId : loggedInCustomerId

  const { data: allCustomers = [] } = useGetCustomersQuery(tenantId || 0, { skip: !isTenant })

  const {
    data: templates = [],
    isLoading,
    isFetching,
  } = useGetAllLoadTemplatesQuery(
    { customerId, tenantId },
    { skip: !tenantId && !customerId },
  )

  const handleCreate = () => {
    setSelectedTemplate(undefined)
    setIsViewMode(false)
    setModalOpen(true)
  }

  const handleEdit = (template: ILoadTemplate) => {
    setSelectedTemplate(template)
    setIsViewMode(false)
    setModalOpen(true)
  }

  const handleView = (template: ILoadTemplate) => {
    setSelectedTemplate(template)
    setIsViewMode(true)
    setModalOpen(true)
  }

  const handleDelete = async (template: ILoadTemplate) => {
    const confirmed = await confirm({
      title: formatMessage(messages.deleteLoadTemplateTitle),
      message: formatMessage(messages.deleteLoadTemplateDescription),
      confirmText: formatMessage(messages.deleteBtn),
      cancelText: formatMessage(messages.cancelBtn),
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        const id = template.id as number
        if (template.customerId) {
          await deleteLoadTemplate({ customerId: template.customerId, id }).unwrap()
          enqueueSuccess(messages.loadTemplateDeleted)
        } else if (template.tenantId) {
          await deleteLoadTemplate({ tenantId: template.tenantId, id }).unwrap()
          enqueueSuccess(messages.loadTemplateDeleted)
        }
      } catch (error) {
        enqueueFailure(messages.loadTemplateDeleteError)
      }
    }
  }

  const columns = useMemo<MRT_ColumnDef<ILoadTemplate>[]>(
    () => [
      {
        accessorKey: 'name',
        header: formatMessage(messages.name).toString(),
      },
      {
        accessorKey: 'isoCode',
        header: formatMessage(messages.isoCode).toString(),
      },
      {
        accessorKey: 'loadType.name',
        header: formatMessage(messages.loadType).toString(),
      },
      {
        accessorFn: (row) => {
          return row.systemDefined ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.isSystemDefined).toString(),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }: any) => {
          return <YesNoTypography isYes={cell.getValue() === 'true'} />
        },
      },
      {
        accessorFn: (row) => {
          return row.inUse ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.inUse).toString(),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }: any) => {
          return <YesNoTypography isYes={cell.getValue() === 'true'} />
        },
      },
    ],
    [formatMessage],
  )

  const customerItems = useMemo(() => {
    return (allCustomers as Array<{ id: number; name?: string; disabled?: boolean }>)
      .filter((customer) => customer.name && !customer.disabled)
      .map((customer) => ({
        id: customer.id,
        name: customer.name || '',
      }))
  }, [allCustomers])

  return (
    <>
      <MRTDataGridV2
        columns={columns}
        data={templates}
        enableRowActions
        positionActionsColumn="last"
        enableColumnActions={false}
        enableColumnFilters={false}
        enableSorting={true}
        initialState={{
          sorting: [
            {
              id: 'name',
              desc: false,
            },
          ],
          density: 'compact',
        }}
        state={{
          isLoading: isLoading || isFetching,
        }}
        renderRowActions={({ row }) => {
          const rowInScope = customerId
            ? !row.original.systemDefined
            : !row.original.customerId && !row.original.systemDefined
          return (
            <Box sx={{ display: 'flex' }}>
              <ViewActionButton onClick={() => handleView(row.original)} />
              <ConditionalRender condition={rowInScope}>
                <EditActionButton onClick={() => handleEdit(row.original)} />
              </ConditionalRender>
              <ConditionalRender condition={!row.original.inUse && rowInScope}>
                <DeleteActionButton
                  onClick={() => handleDelete(row.original)}
                />
              </ConditionalRender>
            </Box>
          )
        }}
        leftHeadingComponent={
          isTenant ? (
            <Box>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  size="small"
                  onClick={() => setSelectedCustomerId(undefined)}
                  sx={{ textTransform: 'none', p: 0 }}
                >
                  {formatMessage(messages.clearSelection)}
                </Button>
              </Box>
              <FormDropdown
                id="customer-filter"
                label={formatMessage(customerMessages.customer)}
                items={customerItems}
                value={selectedCustomerId || ''}
                onChange={(e, _, value) => setSelectedCustomerId(value)}
                sx={{ width: '400px', mt: '12px' }}
              />
            </Box>
          ) : (
            <TableTitleTypography>
              {formatMessage(messages.loadTemplateTableTitle)}
            </TableTitleTypography>
          )
        }
        rightHeadingComponent={
          <AddButton text={formatMessage(messages.add)} onClick={handleCreate} />
        }
      />
      {isModalOpen && (
        <LoadTemplateForm
          isOpen={isModalOpen}
          handleClose={() => setModalOpen(false)}
          isReadOnly={isViewMode}
          loadTemplate={
            selectedTemplate || {
              name: '',
              isoCode: '',
              customerId: customerId,
              tenantId: customerId ? undefined : tenantId,
              isCustomerSelectable: true,
              physicalProperties: {},
            }
          }
        />
      )}
    </>
  )
}

export default LoadTemplateTable
